import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  IFilter,
  IQueryParam,
  IResumenDatos,
  IResumenDatosBase,
} from 'modelos/src';
import {
  DateRangeValue,
  DatePickerComponent,
} from '../../../../../../componentes/date-picker/date-picker.component';
import {
  GraficosComponent,
  IBarChartData,
  ILineChartData,
} from '../../../../../../auxiliares/graficos/graficos.component';
import {
  DtCardComponent,
  DtDatoComponent,
  DtDatosComponent,
} from '../../../../../../componentes/detalle/detalle.components';
import { ChartColors } from '../../../../../../auxiliares/servicios/chart-colors.service';
import { ResumenesDatosService } from '../../../../../../auxiliares/servicios/http/resumenes-datos.service';
import { MetricasCronService } from '../../../../../../auxiliares/servicios/http/metricas-cron.service';
import {
  HelpItem,
  MetricaHelpComponent,
} from '../../../../logs/metricas-downlinks/metrica-help.component';

// Métricas de downlinks de UNA luminaria (histórico persistido en IResumenDatos,
// tipo 'Downlinks Luminaria', agrupacion 'Individual', idsAsignados=[deveui]).
// Va en una tarjeta del detalle: totales del período, gráficos por franja y
// por puerto.
@Component({
  selector: 'app-detalle-metricas-downlinks',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DatePickerComponent,
    GraficosComponent,
    DtCardComponent,
    DtDatosComponent,
    DtDatoComponent,
    MetricaHelpComponent,
  ],
  templateUrl: './detalle-metricas-downlinks.component.html',
  styleUrl: './detalle-metricas-downlinks.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DetalleMetricasDownlinksComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() deveui?: string;

  private _interval?: ReturnType<typeof setInterval>;

  private resumenesDatosService = inject(ResumenesDatosService);
  private metricasService = inject(MetricasCronService);

  // ── MOCK: poné en `true` para previsualizar sin backend. ──
  readonly USAR_MOCK_LUMINARIA = false;

  public hoy = new Date();
  public rango: DateRangeValue = this._rangoDefault();
  public cargando = false;
  public sinDatos = false;

  // Paleta de series: colores de gráfico del tema (ChartColors).
  readonly COL = {
    blue: ChartColors.color1(),
    teal: ChartColors.color6(),
    violet: ChartColors.color4(),
    amber: ChartColors.color3(),
    red: ChartColors.color5(),
    green: ChartColors.color2(),
    gray: ChartColors.neutral(),
  };
  // app-graficos sólo colorea barras de UNA serie si recibe `colorPaleta`.
  readonly paletaBarras = [
    this.COL.blue,
    this.COL.teal,
    this.COL.green,
    this.COL.amber,
    this.COL.violet,
    this.COL.red,
  ];

  // Totales del período (tiles).
  public totAckTrue = 0;
  public totAckFalse = 0;
  public totUplink = 0;
  public totForzados = 0;

  public categorias: string[] = [];
  public serieAckFalse: IBarChartData[] = [];
  public serieConfirmacion: ILineChartData[] = [];

  public catPuertos: string[] = [];
  public seriePuertos: IBarChartData[] = [];

  // ── Textos de ayuda ──────────────────────────────────────────────────────
  readonly helpConfirmacion: HelpItem[] = [
    {
      term: 'ACK true / ACK false',
      desc: 'De los SET enviados a ESTA luminaria: cuántos acusó el dispositivo (ACK true) y cuántos no (ACK false) en el período. ACK false alto indica problemas de bajada (gateway/RF) puntuales de este equipo.',
    },
    {
      term: 'Confirmado x uplink',
      desc: 'De los auto-GET: cuántos se confirmaron porque llegaron los uplinks de configuración esperados.',
    },
    {
      term: 'Forzados',
      desc: 'Pasos que el secuenciador forzó a avanzar tras agotar los reintentos sin recibir confirmación.',
    },
    {
      term: 'Gráfico · ACK false por franja',
      desc: 'Cantidad de SET sin ACK en cada franja horaria del período: muestra CUÁNDO tuvo problemas de bajada esta luminaria.',
    },
    {
      term: 'Gráfico · Confirmación por franja',
      desc: 'Líneas por franja horaria de ACK true, confirmado x uplink y forzados.',
    },
  ];

  readonly helpPuertos: HelpItem[] = [
    {
      term: 'Puerto (aspecto de configuración)',
      desc: 'Cada puerto LoRaWAN corresponde a un aspecto de la config: modo (pto 10), fotocélula (pto 13), dimming (pto 20-29), reportes (pto 43), ACK (pto 44), etc.',
    },
    {
      term: 'ACK false / Uplink timeout',
      desc: 'Por puerto, agregado del período: SET sin ACK y auto-GET que vencieron sin recibir el uplink. Señala QUÉ aspecto de config es el que más falla en esta luminaria.',
    },
  ];

  private _lastDeveui?: string;

  get hayFranjas(): boolean {
    return this.categorias.length > 0;
  }
  get hayPuertos(): boolean {
    return this.catPuertos.length > 0;
  }

  async ngOnChanges(): Promise<void> {
    if (this.deveui && this.deveui !== this._lastDeveui) {
      this._lastDeveui = this.deveui;
      await this.cargar();
    }
  }

  ngOnInit(): void {
    // Auto-refresh: la franja en curso se lee en vivo (Redis) y se mergea.
    this._interval = setInterval((): void => {
      void this.cargar();
    }, 30_000);
  }

  ngOnDestroy(): void {
    clearInterval(this._interval);
  }

  async onRangoChange(rango: DateRangeValue): Promise<void> {
    this.rango = rango;
    await this.cargar();
  }

  async cargar(): Promise<void> {
    if (this.USAR_MOCK_LUMINARIA) {
      const datos = this._mockDatos();
      this.sinDatos = false;
      this._procesar(datos);
      return;
    }
    if (!this.deveui || !this.rango?.start || !this.rango?.end) return;
    this.cargando = true;
    try {
      const filter: IFilter<IResumenDatos> = {
        tipo: 'Downlinks Luminaria',
        idsAsignados: this.deveui,
        periodoInicio: { $gte: this.rango.start, $lte: this.rango.end },
      } as unknown as IFilter<IResumenDatos>;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        limit: 100000,
      };
      const datos = (
        await this.resumenesDatosService.getFiltered<'Downlinks Luminaria'>(
          query,
        )
      ).datos;
      const merged = await this._mergeFranjaActual(datos);
      this.sinDatos = !merged.length;
      this._procesar(merged);
    } finally {
      this.cargando = false;
    }
  }

  // Mergea la franja EN CURSO (Redis en vivo) de esta luminaria sobre las
  // persistidas → tiempo real. Reemplaza la franja de la hora actual.
  private async _mergeFranjaActual(
    persistidos: IResumenDatosBase<'Downlinks Luminaria'>[],
  ): Promise<IResumenDatosBase<'Downlinks Luminaria'>[]> {
    try {
      const franja = await this.metricasService.getFranjaActual();
      const inicio = franja?.periodoInicio;
      if (!inicio) return persistidos;
      if (this.rango?.end && this.rango.end < inicio) return persistidos;
      const mio = franja.luminaria?.find((l) => l.deveui === this.deveui);
      const sinActual = persistidos.filter((d) => d.periodoInicio !== inicio);
      const vivos = mio
        ? [
            {
              tipo: 'Downlinks Luminaria',
              agrupacion: 'Individual',
              agrupacionTiempo: 'Horario',
              periodoInicio: inicio,
              idsAsignados: [this.deveui],
              resumen: mio.resumen,
            } as unknown as IResumenDatosBase<'Downlinks Luminaria'>,
          ]
        : [];
      return [...sinActual, ...vivos];
    } catch {
      return persistidos;
    }
  }

  private _procesar(datos: IResumenDatosBase<'Downlinks Luminaria'>[]): void {
    // Por franja horaria
    const porHora = new Map<
      string,
      { ackTrue: number; ackFalse: number; uplink: number; forzados: number }
    >();
    // Por puerto (agregado en el rango)
    const porPuerto = new Map<
      number,
      { ackFalse: number; ecoTimeout: number }
    >();

    let tAckTrue = 0;
    let tAckFalse = 0;
    let tUplink = 0;
    let tForzados = 0;

    for (const d of datos) {
      const hora = d.periodoInicio;
      const r = d.resumen;
      if (!hora || !r) continue;
      const acc = porHora.get(hora) ?? {
        ackTrue: 0,
        ackFalse: 0,
        uplink: 0,
        forzados: 0,
      };
      acc.ackTrue += r.ackTrue ?? 0;
      acc.ackFalse += r.ackFalse ?? 0;
      acc.uplink += r.confirmadoPorUplink ?? 0;
      acc.forzados += r.forzados ?? 0;
      porHora.set(hora, acc);

      tAckTrue += r.ackTrue ?? 0;
      tAckFalse += r.ackFalse ?? 0;
      tUplink += r.confirmadoPorUplink ?? 0;
      tForzados += r.forzados ?? 0;
      for (const p of r.porPuerto ?? []) {
        const pp = porPuerto.get(p.puerto) ?? { ackFalse: 0, ecoTimeout: 0 };
        pp.ackFalse += p.ackFalse ?? 0;
        pp.ecoTimeout += p.ecoTimeout ?? 0;
        porPuerto.set(p.puerto, pp);
      }
    }

    this.totAckTrue = tAckTrue;
    this.totAckFalse = tAckFalse;
    this.totUplink = tUplink;
    this.totForzados = tForzados;

    const horas = [...porHora.keys()].sort();
    this.categorias = horas.map((h) => this._fmtHora(h));
    this.serieAckFalse = [
      {
        name: 'ACK false',
        color: this.COL.red,
        data: horas.map((h) => porHora.get(h)?.ackFalse ?? 0),
      },
    ];
    this.serieConfirmacion = [
      {
        name: 'ACK true',
        color: this.COL.green,
        data: horas.map((h) => porHora.get(h)?.ackTrue ?? 0),
      },
      {
        name: 'Confirmado x uplink',
        color: this.COL.teal,
        data: horas.map((h) => porHora.get(h)?.uplink ?? 0),
      },
      {
        name: 'Forzados',
        color: this.COL.amber,
        data: horas.map((h) => porHora.get(h)?.forzados ?? 0),
      },
    ];

    const puertos = [...porPuerto.keys()].sort((a, b) => a - b);
    this.catPuertos = puertos.map((p) => 'pto ' + p);
    this.seriePuertos = [
      {
        name: 'ACK false',
        color: this.COL.red,
        data: puertos.map((p) => porPuerto.get(p)?.ackFalse ?? 0),
      },
      {
        name: 'Uplink timeout',
        color: this.COL.amber,
        data: puertos.map((p) => porPuerto.get(p)?.ecoTimeout ?? 0),
      },
    ];
  }

  private _fmtHora(iso: string): string {
    return new Date(iso)
      .toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', '');
  }

  private _rangoDefault(): DateRangeValue {
    const fin = new Date();
    const ini = new Date(fin.getTime() - 7 * 24 * 3600_000);
    return { start: ini.toISOString(), end: fin.toISOString() };
  }

  // Datos de ejemplo (5 franjas + 4 puertos) para previsualizar sin backend.
  private _mockDatos(): IResumenDatosBase<'Downlinks Luminaria'>[] {
    const horas = [
      '2026-07-27T15:00:00.000Z',
      '2026-07-27T16:00:00.000Z',
      '2026-07-27T17:00:00.000Z',
      '2026-07-27T18:00:00.000Z',
      '2026-07-27T19:00:00.000Z',
    ];
    const ackTrue = [8, 6, 5, 7, 9];
    const ackFalse = [5, 7, 6, 2, 1];
    const uplink = [3, 2, 2, 4, 5];
    const forzados = [2, 3, 3, 1, 0];
    return horas.map((h, i) => ({
      tipo: 'Downlinks Luminaria',
      agrupacion: 'Individual',
      agrupacionTiempo: 'Horario',
      periodoInicio: h,
      idsAsignados: [this.deveui ?? 'MOCK-DEVEUI'],
      resumen: {
        ackTrue: ackTrue[i],
        ackFalse: ackFalse[i],
        confirmadoPorUplink: uplink[i],
        forzados: forzados[i],
        porPuerto: [
          {
            puerto: 10,
            enviados: 6,
            ackFalse: [2, 3, 2, 1, 0][i],
            ecoTimeout: [1, 1, 0, 0, 0][i],
          },
          {
            puerto: 13,
            enviados: 4,
            ackFalse: [1, 2, 2, 1, 1][i],
            ecoTimeout: [0, 1, 1, 0, 0][i],
          },
          {
            puerto: 20,
            enviados: 3,
            ackFalse: [1, 1, 1, 0, 0][i],
            ecoTimeout: [1, 2, 1, 1, 0][i],
          },
          {
            puerto: 43,
            enviados: 2,
            ackFalse: [1, 1, 1, 0, 0][i],
            ecoTimeout: [0, 0, 1, 0, 0][i],
          },
        ],
      },
    })) as unknown as IResumenDatosBase<'Downlinks Luminaria'>[];
  }
}
